/**
 * ProfileIconUploader component
 * Wraps ProfileIcon with click-to-upload functionality
 * Allows users to select and upload a custom profile image
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProfileIcon } from '@/components/ProfileIcon';
import { useProfileImageUpload } from '@/hooks/useProfileImageUpload';

export interface ProfileIconUploaderProps {
  /** S3 URL of custom profile image, or null/undefined for default */
  profileImageUrl?: string | null;
  /** Size variant: 'sm' (24px) | 'md' (32px) - default 'md' */
  size?: 'sm' | 'md';
  /** Optional callback invoked after successful upload */
  onUploadSuccess?: (imageUrl: string) => void;
  /** Optional additional classes */
  className?: string;
}

/**
 * ProfileIconUploader - Interactive profile icon with click-to-upload
 * Click to open file picker -> Select image -> Auto-resize -> Upload to S3
 * Shows hover overlay, loading spinner, and error messages
 */
export function ProfileIconUploader({
  profileImageUrl,
  size = 'md',
  onUploadSuccess,
  className,
}: ProfileIconUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const { uploadImage, isUploading, error, isSuccess, uploadedImageUrl, reset } =
    useProfileImageUpload();

  // Handle successful upload
  useEffect(() => {
    if (isSuccess && uploadedImageUrl) {
      onUploadSuccess?.(uploadedImageUrl);
      // Reset success state after a short delay
      const timer = setTimeout(() => reset(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, uploadedImageUrl, onUploadSuccess, reset]);

  /**
   * Resize and crop image to 256x256 using Canvas API
   * Centers the crop for non-square images
   */
  const resizeImage = useCallback((file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          // Create canvas for 256x256 output
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          canvas.width = 256;
          canvas.height = 256;

          // Calculate crop dimensions (centered square from original)
          const minDimension = Math.min(img.width, img.height);
          const sx = (img.width - minDimension) / 2;
          const sy = (img.height - minDimension) / 2;

          // Draw cropped and resized image
          ctx.drawImage(
            img,
            sx,
            sy,
            minDimension,
            minDimension, // Source crop
            0,
            0,
            256,
            256 // Destination size
          );

          // Convert canvas to Blob then File
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob from canvas'));
              return;
            }

            // Preserve original file extension
            const extension = file.name.split('.').pop() || 'jpg';
            const resizedFile = new File([blob], `profile.${extension}`, {
              type: file.type,
              lastModified: Date.now(),
            });

            resolve(resizedFile);
          }, file.type);
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Handle file selection from file picker
   */
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset input value to allow selecting same file again
      e.target.value = '';

      try {
        // Resize image before upload
        const resizedFile = await resizeImage(file);
        uploadImage({ file: resizedFile });
      } catch (err) {
        // If resize fails, fall back to original file
        console.warn('Image resize failed, uploading original:', err);
        uploadImage({ file });
      }
    },
    [resizeImage, uploadImage]
  );

  /**
   * Trigger hidden file input click
   */
  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Determine overlay size classes
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
  };

  // Determine which image URL to display (uploaded or current)
  const displayImageUrl = uploadedImageUrl || profileImageUrl;

  return (
    <div className={cn('relative inline-block', className)}>
      {/* Clickable button wrapper */}
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full',
          sizeClasses[size]
        )}
        aria-label="Upload profile image"
        disabled={isUploading}
      >
        {/* Profile icon */}
        <ProfileIcon imageUrl={displayImageUrl} size={size} />

        {/* Hover overlay with camera icon (desktop only) */}
        {isHovered && !isUploading && (
          <div
            className={cn(
              'absolute inset-0 rounded-full bg-black/40 flex items-center justify-center',
              sizeClasses[size]
            )}
          >
            <Camera className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Loading overlay */}
        {isUploading && (
          <div
            className={cn(
              'absolute inset-0 rounded-full bg-black/60 flex items-center justify-center',
              sizeClasses[size]
            )}
          >
            <Loader2 className="w-3 h-3 text-white animate-spin" />
          </div>
        )}
      </button>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        aria-hidden="true"
      />

      {/* Error message display */}
      {error && <p className="text-xs text-red-500 mt-1 max-w-[200px]">{error.message}</p>}
    </div>
  );
}
